import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { Order } from '../../objects/order';
import { BaseService } from './base.service';
import { map, switchMap } from 'rxjs/operators';
import { CreateOrderPayload, CreateOrderForDeliveryDestinationPayload } from './actions/order.actions';
import { TipUpdatedEvent } from '../cart/cart-tip/cart-tip.component';

@Injectable({
  providedIn: 'root'
})
export class OrderService extends BaseService {
  createInstance = (attributes: Order): Order => ({
    ...attributes,
    bookings: attributes.bookings || [],
    line_items: attributes.line_items || [],
  });

  get(slugOrId: string | number): Observable<Order> {
    return this.getByToken(slugOrId as string);
  }

  
  create(payload: CreateOrderPayload): Observable<Order> {
    let pld = { };

    if (payload instanceof CreateOrderForDeliveryDestinationPayload) {
      pld = {
        order: {
          // TODO: change to warehouse_id
          delivery_warehouse_id: payload.warehouseId,
          delivery: true,
        },
        slug: payload.slug,
        delivery_address: payload.address,
      };
    } else {
      pld = {
        order: {
          destination_id: payload.destinationId
        },
        slug: payload.slug,
      };
    }

    return this.http
      .post<Order>(`${ this.baseUrl }/public/orders/`, pld)
      .pipe(map(this.createInstance))
  }
  

  getByToken(token: string): Observable<Order> {
    return super.doGet<Order, Order>(`orders/current/${token}`);
  }

  getByNumber(payload: {
    orderNumber: string;
  }): Observable<Order> {
    const {
      orderNumber,
    } = payload;

    return super.doGet<Order, Order>(`receipts/${orderNumber}`);
  }

  resetOrderTimeout(payload: {
    order: Order;
  }): Observable<Order> {
    const { order } = payload;

    return this.http
      .put<Order>(`${ this.baseUrl }/public/orders/${order.id}`, {
        ['number']: order.number,
        ['id']: order.id.toString(),
      })
      .pipe(
        switchMap((o: Order) => {
          if (o.id !== order.id) {
            return throwError(() => new Error('Invalid response'));
          }
          
          return of({ ...order, updated_at: o.updated_at } as Order);
          
        }),
      );
  }

  setTip(payload: {
    order: Order;
    tip: TipUpdatedEvent;
  }): Observable<Order> {
    const { tip, order } = payload;

    return this.http
      .put<Order>(`${ this.baseUrl }/public/orders/${order.id}/tip`, {
        ['order_number']: order.number,
        [`${tip.type}`]: tip.value,
      })
      .pipe(
        switchMap((o: Order) => {
          if (o.id !== order.id) {
            return throwError(() => new Error('Invalid response'));
          }

          return this.getByToken(order.token);
        }),
      );
  }

  setPromotion(payload: {
    order: Order;
    code: string;
  }): Observable<Order> {
    const { order, code } = payload;
    const { token } = order;
    const endpoint = `${this.baseUrl}/public/orders/${token}/promotion`;

    return this.http
      .post<Order>(endpoint, { promo_code: code })
      .pipe(
        switchMap((o: Order) => {
          if (o.id !== order.id) {
            return throwError(() => new Error('Invalid response'));
          }

          return this.getByToken(token);
        }),
      );
  }

  removePromotion(payload: {
    order: Order;
  }): Observable<Order> {
    const { order } = payload;
    const { token } = order;
    const endpoint = `${this.baseUrl}/public/orders/${token}/promotion`;

    return this.http
      .delete<Order>(endpoint)
      .pipe(
        switchMap((o: Order) => {
          if (o.id !== order.id) {
            return throwError(() => new Error('Invalid response'));
          }

          return this.getByToken(token);
        }),
      );
  }
}
